import { useMemo } from 'react';
import {
  DEFAULT_PRODUCT_VARIANT_BREAKDOWN,
  getNextValidBreakdowns,
  ProductVariantBreakdownTypes,
} from './utils';
import { useLocalStorageValue } from '@react-hookz/web';

export function useProductVariantBreakdown() {
  const { value: productVariantBreakdown, set: setProductVariantBreakdown } =
    useLocalStorageValue<ProductVariantBreakdownTypes[]>(
      'productVariantBreakdown',
      {
        defaultValue: [],
      },
    );

  const onSelectBreakdown = (breakdown: ProductVariantBreakdownTypes[]) => {
    setProductVariantBreakdown([...breakdown]);
  };

  /**
   * Default implicit breakdowns to keep the UI looking nice and not require the user to select them all the time.
   *
   * [EXPLICIT] -> IMPLICIT
   *
   * [SUPPLIER] -> ENUMERATED_VARIANT
   * [SUPPLIER -> PRODUCT] -> ENUMERATED_VARIANT
   * [PRODUCT] -> ENUMERATED_VARIANT -> SUPPLIER
   * [PRODUCT -> ENUMERATED_VARIANT] -> SUPPLIER
   * [PRODUCT -> SUPPLIER] -> ENUMERATED_VARIANT
   * [ENUMERATED_VARIANT] -> SUPPLIER
   */
  const implicitRestOfBreakdown: ProductVariantBreakdownTypes[] =
    useMemo(() => {
      if (!productVariantBreakdown || !productVariantBreakdown.length)
        return DEFAULT_PRODUCT_VARIANT_BREAKDOWN;

      const root = productVariantBreakdown[0];

      switch (root) {
        case ProductVariantBreakdownTypes.SUPPLIER:
          if (productVariantBreakdown.length === 1) {
            return [ProductVariantBreakdownTypes.ENUMERATED_VARIANT];
          } else if (
            productVariantBreakdown.length === 2 &&
            productVariantBreakdown[1] === ProductVariantBreakdownTypes.PRODUCT
          ) {
            return [ProductVariantBreakdownTypes.ENUMERATED_VARIANT];
          }

          return [];
        case ProductVariantBreakdownTypes.PRODUCT:
          if (productVariantBreakdown.length === 1) {
            return [
              ProductVariantBreakdownTypes.ENUMERATED_VARIANT,
              ProductVariantBreakdownTypes.SUPPLIER,
            ];
          } else if (
            productVariantBreakdown[1] !== ProductVariantBreakdownTypes.SUPPLIER
          ) {
            return [ProductVariantBreakdownTypes.SUPPLIER];
          }

          return getNextValidBreakdowns(productVariantBreakdown).length
            ? [ProductVariantBreakdownTypes.ENUMERATED_VARIANT]
            : [];
        case ProductVariantBreakdownTypes.ENUMERATED_VARIANT:
          return [ProductVariantBreakdownTypes.SUPPLIER];
        default:
          return [];
      }
    }, [productVariantBreakdown]);

  return {
    productVariantBreakdown: productVariantBreakdown || [],
    implicitRestOfBreakdown,
    onSelectBreakdown,
  };
}
